/**
 * @flow
 */

import React, { useCallback, useState } from 'react';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';

import { GET_ADMINISTRATORS_QUERY } from './AdministratorsList';

import type { ModalDialog, Administrator } from '../../../type';

type DeleteAdministratorDialogProps = {
    administrator: Administrator,
} & ModalDialog;

const DELETE_ADMINISTRATOR_QUERY = gql`
    mutation deleteAdministrator($input: DeleteAdministratorInput!) {
        deleteAdministrator(input: $input) {
            administratorId
        }
    }
`;

const DeleteAdministratorDialog = ({
    administrator,
    trigger,
    open,
    onClose,
}: DeleteAdministratorDialogProps) => {
    const userId = useSelector(({ user }) => user.get('id'));
    const salon = useSelector(({ user }) => user.get('salon'));

    const [deleteAdministrator, { loading }] = useMutation(DELETE_ADMINISTRATOR_QUERY);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = useCallback(() => {
        setDeleteFailed(false);
        // No optimisticResponse: the row stays visible while the request is
        // in flight, so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, naturally unmounting
        // this component — no need to call onClose() in .then().
        deleteAdministrator({
            variables: {
                input: {
                    userId,
                    salonId: salon,
                    administratorId: administrator.id,
                },
            },
            update: (store, { data: { deleteAdministrator: result } }) => {
                const data = store.readQuery({
                    query: GET_ADMINISTRATORS_QUERY,
                    variables: {
                        salon,
                    },
                });

                if (
                    data &&
                    data.viewer &&
                    data.viewer.salon &&
                    data.viewer.salon.administrators
                ) {
                    store.writeQuery({
                        query: GET_ADMINISTRATORS_QUERY,
                        variables: {
                            salon,
                        },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    administrators: data.viewer.salon.administrators.filter(
                                        admin =>
                                            admin.id !==
                                            result.administratorId,
                                    ),
                                },
                            },
                        },
                    });
                }
            },
        }).catch(() => {
            setDeleteFailed(true);
        });
    }, [deleteAdministrator, userId, salon, administrator]);

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.administrators.deleteDialog.header"
                    defaultMessage="Delete Administrator"
                />
            </Header>
            <Modal.Content>
                <p>
                    <FormattedMessage
                        id="pages.administrators.deleteDialog.confirmation"
                        defaultMessage="Are you sure you want to delete {administrator}?"
                        values={{
                            administrator: `${administrator.firstName} ${
                                administrator.lastName
                            }`,
                        }}
                    />
                </p>
                {deleteFailed && (
                    <p>
                        <FormattedMessage
                            id="errors.defaultMessage"
                            defaultMessage="Something went wrong"
                        />
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.administrators.deleteDialog.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.administrators.deleteDialog.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteAdministratorDialog;
